<template>
    <HeaderContent title="Guest" />
    <ActionContent
        ctaText="Guest"
        :filterDate="false"
        :filterModal="false"
        @action="createModal"
        @search-query="searchData"
    />
    <GuestStatistic title="Guest Statistics" :data="statistics" />
    <GuestForm
        :modal="modal"
        :selectedData="selectedData"
        @update:modal="updateModal"
        @addOrUpdate="getAll"
    ></GuestForm>

    <DataTable
        :data="guestList"
        :header="headers"
        :custom-actions="customActions"
    >
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import DataTable from "@/components/DataTable.vue";
import { ref, onMounted, inject } from "vue";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import GuestForm from "./GuestForm.vue";
import { formatDateTime } from "@/utils/format";
import GuestStatistic from "@/components/StatisticData.vue";
import iconForeign from '../../../public/icons/user-cowboy.svg';
import iconLocal from '../../../public/icons/people-roof.svg';
import iconOveral from '../../../public/icons/square-poll-vertical.svg';

const auth = useAuthStore();
const $loading = useLoading();
const selectedData = ref({});
const modal = ref(false);
const swal = inject("$swal");
const toast = useToast();

const statistics = ref([]);
const updateStatistics = (summary) => {
    statistics.value = [
        {
            label: "Total",
            key: "Foreign Guest",
            value: summary.foreignGuest ?? 0,
            color: "info",
            icon: iconForeign,
        },
        {
            label: "Total",
            key: "Local Guest",
            value: summary.localGuest ?? 0,
            color: "info",
            icon: iconOveral,
        },
        {
            label: "Total",
            key: "Overall Guest",
            value: summary.totalGuest ?? 0,
            color: "info",
            icon: iconLocal,
        },
        
    ];
};

const headers = ref([
    {
        key: "no",
        label: "No",
    },
    {
        key: "identity_number",
        label: "ID/Passport",
        thClass: "text-left",
        tdClass: "text-left",
        sortable: true,
    },
    {
        key: "fullname_format",
        label: "Fullname",
        thClass: "text-left",
        tdClass: "text-left",
        sortable: true,
    },
    {
        key: "nationality",
        label: "Nationality",
        thClass: "text-left",
        tdClass: "text-left",
        sortable: true,
    },
    {
        key: "phone",
        label: "Contact No",
        thClass: "text-left",
        tdClass: "text-left",
        sortable: true,
    },
    {
        key: "city",
        label: "City",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "address",
        label: "Address",
        sortable: true,
    },
    {
        key: "created_at",
        label: "Created At",
        sortable: true,
    },
    {
        key: "accurate_status",
        label: "Accurate",
        thClass: "text-center",
        tdClass: "text-center",
    },
    {
        key: "action",
        label: "Actions",
    },
]);
const customActions = ref([
    {
        label: "Sync Accurate",
        variant: "info",
        icon: "fas fa-sync",
        action: (item) => {
            syncToAccurate(item.uuid);
        },
        showIf: (item) => !item.accurate_id,
    },
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
            selectedData.value = item;
            modal.value = true;
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this data?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const createModal = () => {
    selectedData.value = {};
    modal.value = true;
};

const updateModal = (newValue) => {
    modal.value = newValue;
};

let guestList = ref([]);

const getAll = async (search = "") => {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/guest`, {
        params: {
            search: search,
        },
        headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + auth._token,
        },
    });

    const res = response.data;

    if (res.status) {
        guestList.value = res.data.map((i) => ({
            ...i,
            fullname_format: `${i.alias ? i.alias + ". " : ""}${i.fullname}`,
            created_at: formatDateTime(i.created_at),
            accurate_status: i.accurate_id
                ? `Synced`
                : `Pending`,
        }));
        updateStatistics(res.summary);
    }
};

const deleteData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/guest/delete/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } finally {
            loader.hide();
        }
    }
};

const syncToAccurate = async (uuid) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.post(
            `${process.env.VUE_APP_API_URL}/guest/sync/${uuid}`,
            {},
            {
                headers: {
                    "Content-type": "application/json",
                    Authorization: "Bearer " + auth._token,
                },
            }
        );

        const res = response.data;

        if (res.status) {
            toast.success("Guest sync to Accurate dispatched!");
            getAll();
        }
    } catch (e) {
        toast.error(e.response?.data?.message ?? "Sync failed");
    } finally {
        loader.hide();
    }
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
